import React, { useMemo, useState } from "react";
import {
  Line,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ComposedChart,
  BarChart,
  Legend,
  ReferenceLine,
  Cell,
} from "recharts";

function parseCurrency(s) {
  if (!s) return null;
  const n = parseFloat(String(s).replace(/[^0-9.-]/g, ""));
  return Number.isFinite(n) ? n : null;
}

function formatYAxis(v) {
  if (v == null) return "";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    notation: "compact",
    maximumFractionDigits: 0,
  }).format(v);
}

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const rows = payload.filter((p) => p.value != null && p.value !== "");

  if (!rows.length) return null;

  return (
    <div className="rounded-md bg-[#7a1f1f] px-3 py-2 text-[12px] text-white shadow-lg">
      <p className="font-semibold mb-0.5">{label}</p>
      {rows.map((p) => (
        <p key={p.dataKey + (p.name || "")} className="text-white/90">
          {p.name}:{" "}
          {p.value != null
            ? new Intl.NumberFormat("en-US", {
                style: "currency",
                currency: "USD",
                maximumFractionDigits: 0,
              }).format(Math.ceil(p.value))
            : "—"}
        </p>
      ))}
    </div>
  );
};

const OpenMonthTick = (props) => {
  const { x, y, payload, openMonth } = props;

  return (
    <g transform={`translate(${x},${y})`}>
      <text
        x={0}
        y={0}
        dy={16}
        textAnchor="end"
        transform="rotate(-45)"
        fill="#64748b"
        fontSize={12}
        fontWeight={500}
      >
        {payload?.value}
      </text>
      {openMonth ? <rect x={-3} y={29} width={10} height={10} fill="#16a34a" rx={1} /> : null}
    </g>
  );
};

const TAB_IDS = {
  ON_TRACK: "onTrack",
  MONTHLY_SPEND: "monthlySpend",
  VARIANCE: "variance",
  YTD: "ytd",
};

const TABS = [
  { id: TAB_IDS.ON_TRACK, label: "Are we on track?" },
  { id: TAB_IDS.MONTHLY_SPEND, label: "Monthly spend vs budget" },
  { id: TAB_IDS.VARIANCE, label: "Monthly variance" },
  { id: TAB_IDS.YTD, label: "YTD spend and forecast" },
];

const COLORS = {
  blue: "#2563eb",
  blueAlt: "#2B60DE",
  grayBar: "#D1D5DB",
  amber: "#F59E0B",
  budgetLine: "#374151",
  forecastBar: "#FBBF24",
  redActual: "#d62728",
  redForecast: "#ff9896",
  greenActual: "#2ca02c",
  greenForecast: "#98df8a",
  refLine: "#9ca3af",
};

function TodayReferenceLabel(props) {
  const { viewBox } = props;
  const cx = (viewBox?.x ?? 0) + (viewBox?.width ?? 0) / 2;
  const y = (viewBox?.y ?? 0) + (viewBox?.height ?? 0) + 18;
  const w = 52;
  const h = 18;
  return (
    <g>
      <rect x={cx - w / 2} y={y - h + 4} width={w} height={h} rx={6} fill="#a51c1c" />
      <text x={cx} y={y} fill="#fff" fontSize={10} fontWeight={600} textAnchor="middle">
        Closed
      </text>
    </g>
  );
}

/** Original YTD chart: monthly budget bars + cumulative projected + monthly actual line */
function YtdSpendForecastChart({ chartData }) {
  return (
    <div style={{ height: 350 }}>
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart data={chartData} margin={{ top: 16, right: 24, left: 8, bottom: 8 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
          <XAxis
            dataKey="month"
            stroke="#94a3b8"
            fontSize={12}
            tick={(tickProps) => (
              <OpenMonthTick
                {...tickProps}
                openMonth={Boolean(chartData[tickProps?.index]?.openMonth)}
              />
            )}
            height={72}
          />
          <YAxis
            stroke="#94a3b8"
            fontSize={12}
            tick={{ fill: "#64748b", fontWeight: 500 }}
            tickFormatter={formatYAxis}
            width={60}
          />
          <Tooltip content={<CustomTooltip />} />
          <Legend wrapperStyle={{ fontSize: 12 }} />
          <Bar dataKey="budget" fill="#cccbf5" name="Monthly Budget" radius={[4, 4, 0, 0]} barSize={18} />
          <Line
            type="monotone"
            dataKey="forecast"
            stroke="#059669"
            strokeWidth={2.5}
            dot={false}
            name="Forecast (EOY Run Rate)"
            connectNulls
          />
          <Line
            type="monotone"
            dataKey="actual"
            stroke={COLORS.blue}
            strokeWidth={2.5}
            dot={{ r: 4 }}
            name="Actual Spend"
            connectNulls={false}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}

function OnTrackChart({ chartData, closedCount, exceedAmount, exceedPct }) {
  const todayX = closedCount > 0 ? chartData[closedCount - 1]?.month : null;

  return (
    <div>
      {exceedAmount != null && exceedAmount > 0 ? (
        <div
          className="mb-3 flex items-center gap-2 rounded-lg px-3 py-2.5 text-sm font-medium"
          style={{ background: "#FEF2F2", color: "#991B1B" }}
        >
          <span aria-hidden>⚠</span>
          <span>
            Projected to exceed annual budget by{" "}
            {new Intl.NumberFormat("en-US", {
              style: "currency",
              currency: "USD",
              maximumFractionDigits: 0,
            }).format(Math.ceil(exceedAmount))}
            {exceedPct != null ? ` (${exceedPct}%)` : ""}
          </span>
        </div>
      ) : null}
      <div style={{ height: 350 }}>
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={chartData} margin={{ top: 16, right: 24, left: 8, bottom: 8 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
            <XAxis dataKey="month" stroke="#94a3b8" fontSize={12} tick={{ fill: "#64748b" }} />
            <YAxis
              stroke="#94a3b8"
              fontSize={12}
              tick={{ fill: "#64748b", fontWeight: 500 }}
              tickFormatter={formatYAxis}
              width={60}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            {todayX ? (
              <ReferenceLine
                x={todayX}
                stroke={COLORS.refLine}
                strokeDasharray="4 4"
                strokeWidth={1}
                label={<TodayReferenceLabel />}
                isFront
              />
            ) : null}
            <Bar
              dataKey="cumulativeBudget"
              fill="#cfd4dc"
              name="Cumulative budget"
              radius={[2, 2, 0, 0]}
              barSize={22}
            />
            <Line
              type="monotone"
              dataKey="cumulativeActual"
              stroke="#3b6ea8"
              strokeWidth={2.5}
              dot={{ r: 4, fill: "#3b6ea8" }}
              name="Actual cumulative"
              connectNulls={false}
            />
            <Line
              type="monotone"
              dataKey="cumulativeProjectedLine"
              stroke="#b45309"
              strokeWidth={2}
              strokeDasharray="6 4"
              dot={{ r: 3.5, fill: "#b45309" }}
              name="Forecast cumulative"
              connectNulls={false}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function MonthlySpendVsBudgetChart({ chartData, closedCount }) {
  const todayX = closedCount > 0 ? chartData[closedCount - 1]?.month : null;

  return (
    <div>
      <p className="text-sm text-gray-600 mb-3 leading-snug">
        Each month&apos;s spend (bars) compared directly to that month&apos;s budget (line). Easy to
        spot which specific months ran over or under plan.
      </p>
      <div style={{ height: 350 }}>
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={chartData} margin={{ top: 16, right: 24, left: 8, bottom: 8 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
            <XAxis dataKey="month" stroke="#94a3b8" fontSize={12} tick={{ fill: "#64748b" }} />
            <YAxis
              stroke="#94a3b8"
              fontSize={12}
              tick={{ fill: "#64748b", fontWeight: 500 }}
              tickFormatter={formatYAxis}
              width={60}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            {todayX ? (
              <ReferenceLine
                x={todayX}
                stroke={COLORS.refLine}
                strokeDasharray="4 4"
                label={<TodayReferenceLabel />}
                isFront
              />
            ) : null}
            <Bar dataKey="actualSpendBar" fill={COLORS.blue} name="Actual spend" radius={[4, 4, 0, 0]} barSize={20} />
            <Bar
              dataKey="forecastSpendBar"
              fill={COLORS.forecastBar}
              name="Forecast spend"
              radius={[4, 4, 0, 0]}
              barSize={20}
            />
            <Line
              type="monotone"
              dataKey="monthlyBudgetLine"
              stroke={COLORS.budgetLine}
              strokeWidth={2}
              dot={{ r: 4, fill: "#fff", stroke: COLORS.budgetLine, strokeWidth: 2 }}
              name="Monthly budget"
              connectNulls
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function VarianceTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const p = payload[0];
  if (p?.value == null) return null;
  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow px-3 py-2 text-xs">
      <p className="font-semibold text-gray-900 mb-1">{label}</p>
      <p style={{ color: p.payload?.varianceColor }}>
        {p.value >= 0 ? "Over budget" : "Under budget"}:{" "}
        {new Intl.NumberFormat("en-US", {
          style: "currency",
          currency: "USD",
          maximumFractionDigits: 0,
        }).format(Math.ceil(Math.abs(p.value)))}
      </p>
    </div>
  );
}

function MonthlyVarianceChart({ chartData, closedCount }) {
  const todayX = closedCount > 0 ? chartData[closedCount - 1]?.month : null;

  return (
    <div>
      <p className="text-sm text-gray-600 mb-3 leading-snug">
        The difference (over or under) between each month&apos;s spend and that month&apos;s budget.
        Red bars are overspend, green bars are underspend. Lighter shades are forecast months.
      </p>
      <div style={{ height: 350 }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={chartData} margin={{ top: 16, right: 24, left: 8, bottom: 8 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
            <XAxis dataKey="month" stroke="#94a3b8" fontSize={12} tick={{ fill: "#64748b" }} />
            <YAxis
              stroke="#94a3b8"
              fontSize={12}
              tick={{ fill: "#64748b", fontWeight: 500 }}
              tickFormatter={formatYAxis}
              width={60}
            />
            <ReferenceLine y={0} stroke="#111827" strokeWidth={1} />
            <Tooltip content={<VarianceTooltip />} />
            {todayX ? (
              <ReferenceLine
                x={todayX}
                stroke={COLORS.refLine}
                strokeDasharray="4 4"
                label={<TodayReferenceLabel />}
                isFront
              />
            ) : null}
            <Bar dataKey="varianceBar" name="Monthly variance" radius={[2, 2, 0, 0]} barSize={22}>
              {chartData.map((entry, i) => (
                <Cell key={`cell-${i}`} fill={entry.varianceColor} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
      <div className="flex flex-wrap justify-center gap-x-6 gap-y-2 mt-2 text-xs text-gray-700">
        <span className="inline-flex items-center gap-2">
          <span className="inline-block h-3 w-3 rounded-sm" style={{ background: COLORS.redActual }} />
          Actual over
        </span>
        <span className="inline-flex items-center gap-2">
          <span className="inline-block h-3 w-3 rounded-sm" style={{ background: COLORS.redForecast }} />
          Forecast over
        </span>
        <span className="inline-flex items-center gap-2">
          <span className="inline-block h-3 w-3 rounded-sm" style={{ background: COLORS.greenActual }} />
          Actual under
        </span>
        <span className="inline-flex items-center gap-2">
          <span className="inline-block h-3 w-3 rounded-sm" style={{ background: COLORS.greenForecast }} />
          Forecast under
        </span>
      </div>
    </div>
  );
}

const Report20YtdBudgetTrendChart = ({
  chartValue,
  closedMonthsCount,
  annualBudget,
  forecastEoySpend,
}) => {
  const [activeTab, setActiveTab] = useState(TAB_IDS.ON_TRACK);

  const { chartDataYtd, chartDataOnTrack, chartDataMonthly, chartDataVariance, exceedAmount, exceedPct } =
    useMemo(() => {
      if (!chartValue?.months) {
        return {
          chartDataYtd: [],
          chartDataOnTrack: [],
          chartDataMonthly: [],
          chartDataVariance: [],
          exceedAmount: null,
          exceedPct: null,
        };
      }

      const closedMonthsNum = Number(closedMonthsCount);
      const closedCount = Number.isFinite(closedMonthsNum)
        ? Math.min(12, Math.max(0, closedMonthsNum))
        : 0;

      const annual = parseCurrency(annualBudget);
      const forecastEoy = parseCurrency(forecastEoySpend);
      let exceedAmountCalc = null;
      let exceedPctCalc = null;
      if (annual != null && forecastEoy != null && forecastEoy > annual) {
        exceedAmountCalc = Math.ceil(forecastEoy - annual);
        exceedPctCalc = annual > 0 ? Math.round((exceedAmountCalc / annual) * 1000) / 10 : null;
      }

      const lastClosedIdx = closedCount > 0 ? closedCount - 1 : -1;

      const chartDataYtd = chartValue.months.map((month, i) => {
        const actual = parseCurrency(chartValue.actual_monthly_spend?.[i]);
        const hasClosedMonths = Number.isFinite(Number(closedMonthsCount));
        const isClosedMonth = hasClosedMonths ? i + 1 <= closedCount : actual != null;

        return {
          month,
          actual,
          forecast: parseCurrency(chartValue.cumulative_projected?.[i]),
          budget: parseCurrency(chartValue.monthly_budget_target?.[i]),
          openMonth: !isClosedMonth,
        };
      });

      const chartDataOnTrack = chartValue.months.map((month, i) => {
        const cumulativeBudget = parseCurrency(chartValue.cumulative_budget?.[i]);
        const cumulativeActual = parseCurrency(chartValue.cumulative_actual?.[i]);
        const cumulativeProjected = parseCurrency(chartValue.cumulative_projected?.[i]);

        let cumulativeProjectedLine = null;
        if (lastClosedIdx >= 0) {
          if (i >= lastClosedIdx) {
            cumulativeProjectedLine = cumulativeProjected;
          }
        } else if (cumulativeProjected != null) {
          cumulativeProjectedLine = cumulativeProjected;
        }

        return {
          month,
          cumulativeBudget,
          cumulativeActual,
          cumulativeProjectedLine,
        };
      });

      const chartDataMonthly = chartValue.months.map((month, i) => {
        const isClosed = i + 1 <= closedCount;
        const actualSpend = parseCurrency(chartValue.actual_monthly_spend?.[i]);
        const forecastSpend = parseCurrency(chartValue.monthly_forecast_spend?.[i]);
        const monthlyBudgetLine = parseCurrency(chartValue.monthly_budget_target?.[i]);

        return {
          month,
          actualSpendBar: isClosed ? actualSpend : null,
          forecastSpendBar: !isClosed ? forecastSpend : null,
          monthlyBudgetLine,
        };
      });

      const chartDataVariance = chartValue.months.map((month, i) => {
        const isClosed = i + 1 <= closedCount;
        let varianceBar = null;
        let varianceColor = COLORS.redForecast;

        if (isClosed) {
          const over = parseCurrency(chartValue.actual_over?.[i]);
          const under = parseCurrency(chartValue.actual_under?.[i]);
          if (over != null && over > 0) {
            varianceBar = over;
            varianceColor = COLORS.redActual;
          } else if (under != null && under > 0) {
            varianceBar = -under;
            varianceColor = COLORS.greenActual;
          }
        } else {
          const over = parseCurrency(chartValue.forecast_over?.[i]);
          const under = parseCurrency(chartValue.forecast_under?.[i]);
          if (over != null && over > 0) {
            varianceBar = over;
            varianceColor = COLORS.redForecast;
          } else if (under != null && under > 0) {
            varianceBar = -under;
            varianceColor = COLORS.greenForecast;
          }
        }

        return { month, varianceBar, varianceColor };
      });

      return {
        chartDataYtd,
        chartDataOnTrack,
        chartDataMonthly,
        chartDataVariance,
        exceedAmount: exceedAmountCalc,
        exceedPct: exceedPctCalc,
      };
    }, [chartValue, closedMonthsCount, annualBudget, forecastEoySpend]);

  if (!chartDataYtd.length) {
    return (
      <div className="h-[350px] flex items-center justify-center text-gray-500 text-sm border border-dashed rounded-xl">
        No chart data
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-end gap-5 mb-3 border-b border-[var(--line,#e4e2dc)]">
        {TABS.map((t) => {
          const selected = activeTab === t.id;
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => setActiveTab(t.id)}
              className={`-mb-px border-b-2 px-0.5 pb-2 text-[13px] transition-colors ${
                selected
                  ? "border-[var(--accent,#a51c1c)] font-semibold text-[var(--accent,#a51c1c)]"
                  : "border-transparent text-[var(--muted,#6b6a66)] hover:text-[var(--ink,#1c1c1a)]"
              }`}
            >
              {t.label}
            </button>
          );
        })}
      </div>

      {activeTab === TAB_IDS.ON_TRACK ? (
        <OnTrackChart
          chartData={chartDataOnTrack}
          closedCount={Number(closedMonthsCount) || 0}
          exceedAmount={exceedAmount}
          exceedPct={exceedPct}
        />
      ) : null}
      {activeTab === TAB_IDS.MONTHLY_SPEND ? (
        <MonthlySpendVsBudgetChart
          chartData={chartDataMonthly}
          closedCount={Number(closedMonthsCount) || 0}
        />
      ) : null}
      {activeTab === TAB_IDS.VARIANCE ? (
        <MonthlyVarianceChart
          chartData={chartDataVariance}
          closedCount={Number(closedMonthsCount) || 0}
        />
      ) : null}
      {activeTab === TAB_IDS.YTD ? <YtdSpendForecastChart chartData={chartDataYtd} /> : null}
    </div>
  );
};

export default Report20YtdBudgetTrendChart;
